/**
 * View image block.
 * @module components/manage/Blocks/Image/View
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  flattenToAppURL,
  isInternalURL,
  withBlockExtensions,
} from '@plone/volto/helpers';
import config from '@plone/volto/registry';

import { OptionalLink } from '@designsystem/stories/01-core/OptionalLink';
import { Media } from '@designsystem/stories/02-atoms/Media';
import { generateImageSizes } from '@fhnw/helpers/DesignSystem/imageSizes';

export const ASPECT_MAP = {
  half: 'none',
  third: 'none',
  large: 'none',
  original: 'none',
};

/**
 * View image block class.
 * @class View
 * @extends Component
 */
export const ImageView = ({ data }) => {
  let href;
  if (data.href?.length > 0) {
    if (typeof data.href === 'object') {
      href = data.href[0]['@id'];
    } else if (typeof data.href === 'string') {
      // just to catch cases where a string might be supplied
      href = data.href;
    }
  }
  const Image = config.getComponent({ name: 'Image' }).component;

  const isOriginalSize = data.size === 'original';

  const actualImageWidth = data.image_scales?.image?.[0]?.width;

  const captionParts = [];
  if (data.caption) {
    captionParts.push(data.caption);
  }
  if (data.description) {
    captionParts.push(data.description);
  }

  const shouldRenderCaption = captionParts.length > 0;

  let caption = null;
  if (shouldRenderCaption) {
    caption = (
      <>
        {captionParts.map((text, i) => (
          <span key={i}>
            {i > 0 && <br />}
            {text}
          </span>
        ))}
      </>
    );
  }

  let imageSizes = {
    '35rem': '100vw',
  };
  let fraction = undefined;
  const containerWidth = data?.align === 'center' ? '45rem' : '106rem';
  if (data?.size === 'large') {
    imageSizes = {
      max: '60rem',
    };
  } else if (data?.size === 'half') {
    fraction = 50;
  } else if (data?.size === 'third') {
    fraction = 33;
  } else if (data?.size === 'portrait') {
    fraction = 20;
  }

  if (fraction) {
    imageSizes[containerWidth] = `${fraction}vw`;
    imageSizes['max'] = `${fraction}rem`;
  }

  const sizes = generateImageSizes(imageSizes);

  return (
    <>
      <OptionalLink
        href={href}
        target={data.openLinkInNewTab ? '_blank' : undefined}
      >
        <Media
          aspect={ASPECT_MAP?.[data?.format] || data?.format}
          caption={shouldRenderCaption && caption}
          focus_point={data?.focus_point}
          className={`media--align-${data?.align} media--size-${data?.size}${data?.float ? ' media--float' : ''}`}
          style={
            isOriginalSize
              ? {
                  maxWidth: actualImageWidth,
                }
              : {}
          }
        >
          {data.url && (
            <Image
              className="media__object"
              item={
                data.image_scales
                  ? {
                      '@id': data.url,
                      image_field: data.image_field,
                      image_scales: data.image_scales,
                    }
                  : undefined
              }
              src={
                data.image_scales
                  ? undefined
                  : isInternalURL(data.url)
                    ? // Backwards compat in the case that the block is storing the full server URL
                      (() => {
                        if (data.size === 'large')
                          return `${flattenToAppURL(data.url)}/@@images/image`;
                        if (data.size === 'half')
                          return `${flattenToAppURL(
                            data.url,
                          )}/@@images/image/preview`;
                        if (data.size === 'third' || data.size === 'portrait')
                          return `${flattenToAppURL(
                            data.url,
                          )}/@@images/image/mini`;
                        return `${flattenToAppURL(data.url)}/@@images/image`;
                      })()
                    : data.url
              }
              // START CUSTOMIZATION
              sizes={sizes}
              // END CUSTOMIZATION
              alt={data.alt || ''}
              loading="lazy"
              responsive={true}
            />
          )}
        </Media>
      </OptionalLink>
      {data.block_description && <p>{data.block_description}</p>}
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
ImageView.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withBlockExtensions(ImageView);
